import { Injectable } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Actions, ofActionCompleted, Store } from '@ngxs/store';
import { combineLatest, filter, map, Observable, tap } from 'rxjs';

import { ActivityApiService } from '@hiptraveler/data-access/api';
import { AuthAction, AuthState } from '@hiptraveler/data-access/auth';
import { UserState } from '@hiptraveler/data-access/user';
import { ExperienceFinderStateService } from '@hiptraveler/features/experience-finder';
import { brandCampaignId, clientVID, getWindowRef } from '@hiptraveler/common';

@Injectable()
export class WidgetObserverStateService {

  constructor(
    private router: Router,
    private location: Location,
    private store: Store,
    private actions$: Actions,
    private activityApi: ActivityApiService,
    private stateService: ExperienceFinderStateService
  ) { }

  get routerEnd$(): Observable<NavigationEnd> {
    return this.router.events.pipe(
      filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd)
    );
  }

  /**
   * Registers a callback to run when the user has successfully signed out.
   *
   * Listens for completion of both SignOut and SignOutNoAuth actions,
   * filtering only for successful, non-error, non-canceled outcomes.
   */
  registerAuthSignOutHandler(onSuccess: () => void): void {
    this.actions$.pipe(
      ofActionCompleted(AuthAction.SignOut, AuthAction.SignOutNoAuth),
      filter(state => {
        return state.result.successful && !state.result.error && !state.result.canceled;
      }),
      tap(() => onSuccess())
    ).subscribe()
  }

  /**
   * Listens for authentication state and invokes the callback when a valid access token is available.
   */
  listenForAuthenticatedAccessToken(callback: (accessToken: string | null) => void): void {
    combineLatest([
      this.store.select(AuthState.accessToken),
      this.store.select(UserState.authenticated)
    ]).pipe(
      filter(([ accessToken, authenticated ]) => !!accessToken && authenticated),
      tap(
        ([ accessToken ]) => callback(accessToken)
      )
    ).subscribe();
  }

  /**
   * Tracks and reports widget events posted via window.postMessage.
   *
   * Captures the client visitor ID, brand campaign ID, and the current window URL,
   * then sends a standardized 'widget' action event to the analytics API.
   */
  public trackWidgetPostMessageEvent(): void {
    this.activityApi.setUserAction({
      objType: 'widget',
      actionType: 'widget',
      vId: clientVID() || undefined,
      cId: brandCampaignId(),
      source: getWindowRef()?.location?.href
    }).subscribe()
  }

  /**
   * Sets up synchronization of URL changes by invoking the provided callback.
   *
   * Subscribes to both the Location service’s `onUrlChange` and Angular Router’s
   * `NavigationEnd` events, calling `urlSyncCallback` with the new URL whenever
   * navigation occurs.
   */
  setupUrlSyncListener(urlSyncCallback: (url: string) => void): void {
  
    // Listen to low-level location changes (e.g., browser history API)
    this.location.onUrlChange(urlSyncCallback.bind(this));

    // Listen to Angular Router navigation end events
    this.routerEnd$.pipe(
      map((event: NavigationEnd) => event.url)
    ).subscribe(urlSyncCallback.bind(this));
  }

  /**
   * Opens the Experience Finder overlay and switches the UI to the "Plan a Trip" view.
   *
   * - If the overlay is currently closed, enables it.
   * - Sets the dialog view to "main-menu".
   * - Sets the main menu view to "plan_a_trip".
   */
  openPlanTripExperienceFinderPanel(): void {
    const overlayState = this.stateService.overlayState$$;
    if (!overlayState.value) {
      overlayState.next(true);
    }
    this.stateService.dialogView$$.next('main-menu');
    this.stateService.mainMenuView$$.next('plan_a_trip');
  }

}
